import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Scale, Minus, Plus, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatPrice, CATEGORY_NAMES } from "../lib/api";
import { useShop } from "../store/ShopContext";
import { ProductCard } from "../components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWishlisted, toggleCompare, isCompared } = useShop();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [sticky, setSticky] = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setActive(0);
    setQty(1);
    window.scrollTo({ top: 0 });
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      const p = r.data.product;
      setVariant(p.variants.find((v) => v.slug === slug)?.name || p.variants[0]?.name || p.color || null);
    }).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setSticky(!e.isIntersecting), { threshold: 0 });
    if (ctaRef.current) obs.observe(ctaRef.current);
    return () => obs.disconnect();
  }, [data]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="container-cc py-32 text-center">
        <p className="font-serif text-4xl">Produsul nu a fost găsit</p>
        <Link to="/catalog" className="btn-outline mt-8">Înapoi la catalog</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container-cc py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 surface aspect-square animate-pulse" />
        <div className="lg:col-span-5 space-y-4"><div className="h-4 w-24 surface animate-pulse" /><div className="h-12 w-3/4 surface animate-pulse" /><div className="h-6 w-32 surface animate-pulse" /></div>
      </div>
    );
  }

  const { product: p, related } = data;
  const images = p.images;
  const wished = isWishlisted(p.slug);
  const compared = isCompared(p.slug);

  const selectVariant = (v) => {
    if (v.slug && v.slug !== slug) return navigate(`/produs/${v.slug}`);
    setVariant(v.name);
    const idx = v.image ? images.indexOf(v.image) : 0;
    setActive(idx >= 0 ? idx : 0);
  };

  const add = () => addToCart(p, variant, qty);

  return (
    <div data-testid="product-page" className="pb-24 lg:pb-0">
      <div className="container-cc pt-6 lg:pt-10">
        <nav className="text-[11px] uppercase tracking-[0.18em] text-[#737373] flex items-center gap-2 mb-8">
          <Link to="/" className="hover:text-[#0A0A0A]">Acasă</Link><span>/</span>
          <Link to={`/catalog/${p.category}`} className="hover:text-[#0A0A0A]">{CATEGORY_NAMES[p.category]}</Link><span>/</span>
          <span className="text-[#0A0A0A]">{p.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-[120px]">
              <div className="relative surface aspect-square overflow-hidden" data-testid="product-gallery">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={images[active]}
                    src={images[active]}
                    alt={`${p.name} ${active + 1}`}
                    className="product-img absolute inset-0 w-full h-full object-contain p-8 sm:p-14"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                  />
                </AnimatePresence>
                {p.badge && <span className="badge-cc absolute top-5 left-5">{p.badge}</span>}
                {images.length > 1 && (
                  <>
                    <button data-testid="gallery-prev" aria-label="Anterior" onClick={() => setActive((active - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/90 flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors"><ChevronLeft size={18} /></button>
                    <button data-testid="gallery-next" aria-label="Următor" onClick={() => setActive((active + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/90 flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors"><ChevronRight size={18} /></button>
                  </>
                )}
                <span className="absolute bottom-4 right-5 text-[11px] tracking-[0.2em] text-[#737373]">{String(active + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar">
                  {images.map((src, i) => (
                    <button key={src} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)} className={`surface h-20 w-20 shrink-0 flex items-center justify-center p-2 border transition-colors duration-200 ${i === active ? "border-[#0A0A0A]" : "border-transparent hover:border-black/30"}`}>
                      <img src={src} alt="" className="product-img max-h-full object-contain" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <p className="eyebrow">{p.collection} · {p.sku}</p>
              <h1 data-testid="product-title" className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-3 leading-[1.05]">{p.name}</h1>
              {p.color && <p data-testid="product-color" className="text-sm text-[#737373] mt-2">{variant || p.color}</p>}
              <p className="mt-5 text-[#5A5A5A] leading-relaxed">{p.tagline}</p>
              <p data-testid="product-price" className="mt-6 text-2xl font-semibold">{formatPrice(p.price)}</p>
              <p className="text-xs text-[#9A9A9A] mt-1">Preț demo, TVA inclus. Disponibilitatea și livrarea vor fi confirmate.</p>

              {p.variants.length > 0 && (
                <div className="mt-8" data-testid="variant-selector">
                  <p className="eyebrow mb-3">Culoare — <span className="text-[#0A0A0A] normal-case tracking-normal font-medium">{variant}</span></p>
                  <div className="flex flex-wrap gap-2">
                    {p.variants.map((v) => {
                      const selected = v.name === variant;
                      return (
                        <button key={v.name} data-testid={`variant-${v.name.replace(/\s/g, "-").toLowerCase()}`} aria-label={v.name} title={v.name} onClick={() => selectVariant(v)} className={`h-10 w-10 flex items-center justify-center border transition-colors duration-200 ${selected ? "border-[#0A0A0A]" : "border-black/15 hover:border-black/50"}`}>
                          <span className="h-6 w-6" style={{ backgroundColor: v.hex }} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div ref={ctaRef} className="mt-8 flex gap-3">
                <div className="inline-flex items-center border border-black/20 h-12">
                  <button data-testid="qty-dec" aria-label="Scade" className="h-full w-11 flex items-center justify-center hover:bg-[#F2F2EF]" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={14} /></button>
                  <span data-testid="qty-value" className="w-10 text-center text-sm font-medium">{qty}</span>
                  <button data-testid="qty-inc" aria-label="Crește" className="h-full w-11 flex items-center justify-center hover:bg-[#F2F2EF]" onClick={() => setQty(qty + 1)}><Plus size={14} /></button>
                </div>
                <button data-testid="add-to-cart-btn" onClick={add} className="btn-primary flex-1">Adaugă în coș</button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <button data-testid="product-wishlist-btn" onClick={() => toggleWishlist(p)} className={`h-12 border text-[12px] uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 transition-colors duration-200 ${wished ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#0A0A0A]"}`}>
                  <Heart size={15} fill={wished ? "currentColor" : "none"} /> {wished ? "În favorite" : "Favorite"}
                </button>
                <button data-testid="product-compare-btn" onClick={() => toggleCompare(p)} className={`h-12 border text-[12px] uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 transition-colors duration-200 ${compared ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/20 hover:border-[#0A0A0A]"}`}>
                  <Scale size={15} /> {compared ? "În comparare" : "Compară"}
                </button>
              </div>

              {p.highlights.length > 0 && (
                <ul className="mt-10 space-y-3">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex gap-3 text-sm text-[#3A3A3A]"><Check size={16} strokeWidth={1.5} className="text-[#B4A38E] shrink-0 mt-0.5" />{h}</li>
                  ))}
                </ul>
              )}

              <Accordion type="multiple" defaultValue={["desc"]} className="mt-10 border-t border-black/10">
                <AccordionItem value="desc" className="border-b border-black/10">
                  <AccordionTrigger data-testid="acc-description" className="font-serif text-xl font-normal py-5 hover:no-underline">Descriere</AccordionTrigger>
                  <AccordionContent className="text-[#5A5A5A] leading-relaxed text-sm sm:text-base pb-6">
                    <p>{p.description}</p>
                    {p.sketches?.length > 0 && (
                      <div data-testid="product-sketches" className="mt-6 space-y-4">
                        <p className="eyebrow">Dimensiuni & schițe</p>
                        {p.sketches.map((src, i) => (
                          <img key={src} data-testid={`sketch-${i}`} src={src} alt={`${p.name} – schiță ${i + 1}`} loading="lazy" className="w-full bg-white border border-black/5 p-3" />
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
                {p.details?.length > 0 && (
                  <AccordionItem value="details" className="border-b border-black/10">
                    <AccordionTrigger data-testid="acc-details" className="font-serif text-xl font-normal py-5 hover:no-underline">Detalii de la producător</AccordionTrigger>
                    <AccordionContent className="pb-6">
                      <ul data-testid="product-details" className="space-y-2.5 text-sm text-[#3A3A3A] leading-relaxed">
                        {p.details.map((t, i) => (
                          <li key={i} className="flex gap-3"><span className="mt-2 h-1 w-1 shrink-0 bg-[#B4A38E]" />{t}</li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
                <AccordionItem value="specs" className="border-b border-black/10">
                  <AccordionTrigger data-testid="acc-specs" className="font-serif text-xl font-normal py-5 hover:no-underline">Specificații tehnice</AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <table data-testid="specs-table" className="w-full text-sm">
                      <tbody>
                        {p.specs.map(([k, v]) => (
                          <tr key={k} className="border-b border-black/5 last:border-0">
                            <td className="py-2.5 pr-4 text-[#737373] align-top w-1/2">{k}</td>
                            <td className="py-2.5 font-medium">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="ship" className="border-b border-black/10">
                  <AccordionTrigger data-testid="acc-shipping" className="font-serif text-xl font-normal py-5 hover:no-underline">Livrare & retur</AccordionTrigger>
                  <AccordionContent className="text-[#5A5A5A] leading-relaxed text-sm pb-6">
                    Acesta este un demo de prezentare. Condițiile de livrare, retur și garanție vor fi definite și afișate aici înainte de lansarea magazinului.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="container-cc mt-24 lg:mt-32" data-testid="related-products">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="eyebrow mb-3">Completează</p>
              <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">Produse similare</h2>
            </div>
            <Link to={`/catalog/${p.category}`} className="btn-ghost hidden sm:inline-flex">Vezi categoria</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {related.map((r, i) => <ProductCard key={r.slug} product={r} index={i} />)}
          </div>
        </section>
      )}

      <AnimatePresence>
        {sticky && (
          <motion.div data-testid="sticky-add-to-cart" initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={{ type: "spring", stiffness: 300, damping: 30 }} className="fixed bottom-0 inset-x-0 z-30 glass border-t border-black/10 px-4 py-3 flex items-center gap-3 lg:hidden">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name}</p>
              <p className="text-sm font-semibold">{formatPrice(p.price)}</p>
            </div>
            <button data-testid="sticky-add-to-cart-btn" onClick={add} className="btn-primary h-11 px-5">Adaugă în coș</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
